import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdCloudUpload, MdArrowBack } from "react-icons/md";
import { FaSave } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";

const EditTestimoni = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    role: "",
    content: "",
    avatar_url: "",
  });

  /**

* Header API
  */
  const getHeaders = () => ({
    Authorization: localStorage.getItem("token"),
    db_name: process.env.REACT_APP_DB_NAME,
  });

  /**

* Ambil data testimoni berdasarkan ID
  */
  useEffect(() => {
    const fetchTestimoni = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`/api_w/testimonials/${id}`, {
          headers: getHeaders(),
        });

        console.log("Response detail testimoni:", response.data);

        /**
         * Ambil data berdasarkan struktur API
         */
        const data = response.data?.data;

        if (!data) {
          toast.error("Data testimoni tidak ditemukan");

          navigate("/admin/testimoni", {
            replace: true,
          });

          return;
        }

        setFormData({
          name: data.name || "",
          role: data.role || "",
          content: data.content || "",
          avatar_url: data.avatar_url || "",
        });
      } catch (error) {
        console.error("Error mengambil data testimoni:", error);

        toast.error(
          error.response?.data?.message || "Gagal mengambil data testimoni"
        );

        navigate("/admin/testimoni", {
          replace: true,
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTestimoni();
    }
  }, [id, navigate]);

  /**

* Handle perubahan input
  */
  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**

* Upload avatar
  */
  const handleUpload = async (file) => {
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran file tidak boleh lebih dari 5MB");

      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");

      return;
    }

    setUploading(true);

    try {
      const uploadData = new FormData();

      uploadData.append("file", file);

      const response = await axios.post("/api_w/upload/single", uploadData, {
        headers: {
          ...getHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Response upload:", response.data);

      let imageUrl = null;

      if (response.data?.data?.Location) {
        imageUrl = response.data.data.Location;
      } else if (response.data?.data?.url) {
        imageUrl = response.data.data.url;
      } else if (response.data?.data?.file) {
        imageUrl = response.data.data.file;
      } else if (response.data?.url) {
        imageUrl = response.data.url;
      } else if (response.data?.Location) {
        imageUrl = response.data.Location;
      } else if (response.data?.file) {
        imageUrl = response.data.file;
      } else if (typeof response.data === "string") {
        imageUrl = response.data;
      }

      if (!imageUrl) {
        toast.error("Gagal mendapatkan URL foto dari server");

        return;
      }

      /**
       * Jika URL relatif
       */
      if (!imageUrl.startsWith("http://") && !imageUrl.startsWith("https://")) {
        if (imageUrl.startsWith("/")) {
          imageUrl = `${window.location.origin}${imageUrl}`;
        } else {
          imageUrl = `https://${imageUrl}`;
        }
      }

      setFormData((prev) => ({
        ...prev,
        avatar_url: imageUrl,
      }));

      toast.success("Foto berhasil diunggah");
    } catch (error) {
      console.error("Error upload avatar:", error);

      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Gagal mengupload foto"
      );
    } finally {
      setUploading(false);
    }
  };

  /**

* Handle pilih file
  */
  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (file) {
      handleUpload(file);
    }
  };

  /**

* Submit update testimoni
  */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.name.trim()) {
      toast.warning("Nama wajib diisi");

      return;
    }

    if (!formData.role.trim()) {
      toast.warning("Role / Jabatan wajib diisi");

      return;
    }

    if (!formData.content.trim()) {
      toast.warning("Testimoni wajib diisi");

      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: formData.name.trim(),
        role: formData.role.trim(),
        content: formData.content.trim(),
        avatar_url: formData.avatar_url || null,
      };

      console.log("Payload update testimoni:", payload);

      const response = await axios.put(`/api_w/testimonials/${id}`, payload, {
        headers: {
          ...getHeaders(),
          "Content-Type": "application/json",
        },
      });

      console.log("Response update testimoni:", response.data);

      toast.success("Testimoni berhasil diperbarui");

      /**
       * Beri waktu agar toast tampil,
       * kemudian kembali ke halaman index.
       *
       * refresh digunakan sebagai penanda
       * agar index mengambil ulang data.
       */
      setTimeout(() => {
        navigate("/admin/testimoni", {
          replace: true,
          state: {
            refresh: Date.now(),
            updatedId: id,
          },
        });
      }, 800);
    } catch (error) {
      console.error("Error update testimoni:", error);

      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Gagal memperbarui testimoni"
      );
    } finally {
      setSaving(false);
    }
  };

  /**

* Loading
  */
  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="border-r-transparent mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-500" />

          <p className="mt-3 text-sm text-gray-500">Memuat data testimoni...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 p-6">
      {" "}
      <div className="mx-auto max-w-5xl rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* Header */}{" "}
        <div className="flex items-center justify-between border-b border-gray-200 px-8 py-6">
          {" "}
          <div>
            {" "}
            <h2 className="text-xl font-bold text-gray-900">Edit Testimoni </h2>
            <p className="mt-1 text-sm text-gray-500">
              Perbarui informasi testimoni
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/admin/testimoni")}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
          >
            <MdArrowBack className="h-5 w-5" />
            Kembali
          </button>
        </div>
        {/* Form */}
        <form onSubmit={handleSubmit} className="px-8 py-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
            {/* Avatar */}
            <div className="flex flex-col items-center lg:col-span-1">
              <label className="mb-4 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                Foto
              </label>

              <label className="group relative flex h-32 w-32 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-blue-400 hover:bg-blue-50">
                {formData.avatar_url ? (
                  <>
                    <img
                      src={formData.avatar_url}
                      alt={formData.name || "Testimoni"}
                      className="h-full w-full object-cover transition-opacity group-hover:opacity-75"
                    />

                    <div className="bg-black/0 group-hover:bg-black/30 absolute inset-0 flex items-center justify-center transition-all">
                      <span className="text-xs font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100">
                        Ganti
                      </span>
                    </div>
                  </>
                ) : uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="border-r-transparent h-6 w-6 animate-spin rounded-full border-4 border-blue-500" />

                    <span className="text-xs text-gray-500">Uploading...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <MdCloudUpload className="h-8 w-8 text-gray-400" />

                    <span className="text-xs text-gray-500">Upload</span>
                  </div>
                )}

                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
              </label>

              <p className="mt-3 text-center text-xs text-gray-400">
                Maksimal 5MB
              </p>
            </div>

            {/* Form Fields */}
            <div className="space-y-5 lg:col-span-3">
              {/* Nama */}
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Nama
                </label>

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Masukkan nama"
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {/* Role */}
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Role / Jabatan
                </label>

                <input
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  placeholder="Contoh: Guru, Siswa, Alumni"
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {/* Content */}
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Testimoni / Konten
                </label>

                <textarea
                  name="content"
                  rows={6}
                  value={formData.content}
                  onChange={handleChange}
                  placeholder="Masukkan testimoni atau ulasan"
                  className="w-full resize-none rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>
          </div>

          {/* Action */}
          <div className="mt-8 flex justify-end gap-3 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={() => navigate("/admin/testimoni")}
              className="rounded-lg border border-gray-300 px-6 py-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
            >
              Batal
            </button>

            <button
              type="submit"
              disabled={saving || uploading}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              <FaSave className="h-4 w-4" />

              {saving ? "Menyimpan..." : "Simpan Perubahan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTestimoni;
